import React, { useState, useEffect } from "react";
import { getTestCase, createTestCase, updateTestCase } from "../api/testCases.js";
import { listPatients as getPatients } from "../api/patients.js";
import { apiPost } from "../api/client.js";

// AI generation — calls the server endpoint (will be fully implemented in a later phase)
const generateTestCase = (goal, patientId) => apiPost("/api/v1/test-cases/generate", { goal, patientId });

const EMPTY_PERSONA = { firstName: "", lastName: "", dob: "" };

export default function TestCaseEditor({ navigate, testCaseId }) {
  const isEdit = !!testCaseId;
  const [patients, setPatients] = useState([]);
  // scenario: "existing" (pick an existing patient) or "new" (inline persona)
  const [form, setForm] = useState({
    name: "",
    description: "",
    scenario: "existing",
    patientId: "PAT-001",
    persona: { ...EMPTY_PERSONA },
    taskDescription: "",
    expectedOutcomes: [""],
    tags: [],
    enabled: true,
  });
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [aiGoal, setAiGoal] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiOpen, setAiOpen] = useState(!isEdit); // open by default for new test cases

  useEffect(() => {
    getPatients().then(setPatients).catch(console.error);
    if (isEdit) {
      getTestCase(testCaseId).then((tc) => {
        // If the saved test case has an inline persona, load as "new"
        // scenario; otherwise load as "existing" with the stored patientId.
        const hasPersona = tc.persona && tc.persona.firstName && tc.persona.lastName && tc.persona.dob;
        setForm({
          name: tc.name || "",
          description: tc.description || "",
          scenario: hasPersona ? "new" : "existing",
          patientId: tc.patientId || "PAT-001",
          persona: hasPersona
            ? {
                firstName: tc.persona.firstName,
                lastName: tc.persona.lastName,
                dob: tc.persona.dob,
                condition: tc.persona.condition || "",
                treatment: tc.persona.treatment || "",
                physician: tc.persona.physician || "",
              }
            : { ...EMPTY_PERSONA },
          taskDescription: tc.taskDescription || "",
          expectedOutcomes: tc.expectedOutcomes?.length > 0 ? tc.expectedOutcomes : [""],
          tags: tc.tags || [],
          enabled: tc.enabled !== false,
        });
      }).catch(console.error);
    }
  }, [testCaseId, isEdit]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPersonaField = (k, v) => setForm((f) => ({ ...f, persona: { ...f.persona, [k]: v } }));

  const handleOutcomeChange = (idx, val) => {
    const outcomes = [...form.expectedOutcomes];
    outcomes[idx] = val;
    setField("expectedOutcomes", outcomes);
  };

  const addOutcome = () => setField("expectedOutcomes", [...form.expectedOutcomes, ""]);
  const removeOutcome = (idx) => {
    const outcomes = form.expectedOutcomes.filter((_, i) => i !== idx);
    setField("expectedOutcomes", outcomes.length > 0 ? outcomes : [""]);
  };

  const addTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      setField("tags", [...form.tags, tagInput.trim()]);
      setTagInput("");
    }
  };
  const removeTag = (tag) => setField("tags", form.tags.filter((t) => t !== tag));

  const handleGenerate = async () => {
    if (!aiGoal.trim()) return;
    setAiGenerating(true);
    try {
      const result = await generateTestCase(aiGoal.trim(), form.patientId);
      setForm((f) => ({
        ...f,
        name: result.name || f.name,
        description: result.description || f.description,
        taskDescription: result.taskDescription || f.taskDescription,
        expectedOutcomes: result.expectedOutcomes?.length > 0 ? result.expectedOutcomes : f.expectedOutcomes,
        tags: result.tags?.length > 0 ? result.tags : f.tags,
      }));
      setAiOpen(false); // collapse after generation
    } catch (err) {
      alert("Generation failed: " + err.message);
    } finally {
      setAiGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) return alert("Name is required");
    if (!form.taskDescription.trim()) return alert("Task description is required");

    // Build the payload based on the scenario. Send persona XOR patientId
    // so the server knows which binding the user actually chose, and the
    // unused side gets cleared out (prevents the clobber bug where an
    // editor default leaks into a persona-based test case).
    let patientBinding;
    if (form.scenario === "new") {
      const p = form.persona;
      if (!p.firstName.trim() || !p.lastName.trim() || !p.dob.trim()) {
        return alert("New-patient scenarios require first name, last name, and date of birth");
      }
      patientBinding = {
        patientId: null,
        persona: {
          firstName: p.firstName.trim(),
          lastName: p.lastName.trim(),
          dob: p.dob.trim(),
          condition: p.condition?.trim() || undefined,
          treatment: p.treatment?.trim() || undefined,
          physician: p.physician?.trim() || undefined,
        },
      };
    } else {
      if (!form.patientId) {
        return alert("Please select a patient");
      }
      patientBinding = { patientId: form.patientId, persona: null };
    }

    setSaving(true);
    try {
      const data = {
        name: form.name,
        description: form.description,
        ...patientBinding,
        taskDescription: form.taskDescription,
        expectedOutcomes: form.expectedOutcomes.filter((o) => o.trim()),
        tags: form.tags,
        enabled: form.enabled,
      };
      if (isEdit) await updateTestCase(testCaseId, data);
      else await createTestCase(data);
      navigate("dashboard");
    } catch (err) {
      alert("Save failed: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const selectedPatient = patients.find((p) => p.id === form.patientId);

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <button style={s.back} onClick={() => navigate("dashboard")}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        </button>
        <h1 style={s.title}>{isEdit ? "Edit Test Case" : "New Test Case"}</h1>
      </div>

      <div style={s.body}>
        {/* AI Generation Section */}
        <div style={s.form}>
          <div style={s.aiSection}>
            <button style={s.aiToggle} onClick={() => setAiOpen(!aiOpen)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2a4 4 0 0 0-4 4c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2 4 4 0 0 0-4-4z"/>
                <path d="M12 8v8"/><path d="M8 12h8"/>
                <circle cx="12" cy="20" r="2"/>
              </svg>
              Generate with AI
              <span style={s.aiChevron}>{aiOpen ? "\u25B4" : "\u25BE"}</span>
            </button>

            {aiOpen && (
              <div style={s.aiBody}>
                <div style={s.aiHint}>
                  Describe what you want to test and the AI will generate the test case fields. Select a patient first, then describe the goal.
                </div>
                <textarea
                  style={s.aiInput}
                  value={aiGoal}
                  onChange={(e) => setAiGoal(e.target.value)}
                  placeholder="e.g., Patient calls to reschedule their PET/CT Scan to next month because they're traveling. They should pick the earliest available slot."
                  rows={3}
                />
                <button
                  style={aiGenerating ? s.aiGenBtnDisabled : s.aiGenBtn}
                  onClick={handleGenerate}
                  disabled={aiGenerating || !aiGoal.trim()}
                >
                  {aiGenerating ? (
                    <><span style={s.aiSpinner} /> Generating...</>
                  ) : (
                    "Generate Test Case"
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        <div style={s.form}>
          {/* Name + Enabled */}
          <div style={s.row}>
            <div style={s.fieldFull}>
              <label style={s.label}>Name</label>
              <input style={s.input} value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="e.g., Schedule Treatment Visit" />
            </div>
            <div style={s.fieldSmall}>
              <label style={s.label}>Enabled</label>
              <select style={s.input} value={form.enabled ? "yes" : "no"} onChange={(e) => setField("enabled", e.target.value === "yes")}>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
          </div>

          {/* Description */}
          <div style={s.field}>
            <label style={s.label}>Description</label>
            <input style={s.input} value={form.description} onChange={(e) => setField("description", e.target.value)} placeholder="Brief description of what this test validates" />
          </div>

          {/* Patient binding: existing patient OR new-patient persona */}
          <div style={s.field}>
            <label style={s.label}>Patient</label>
            <div style={s.scenarioTabs}>
              <button
                type="button"
                style={form.scenario === "existing" ? s.scenarioTabActive : s.scenarioTab}
                onClick={() => setField("scenario", "existing")}
              >
                Existing Patient
              </button>
              <button
                type="button"
                style={form.scenario === "new" ? s.scenarioTabActive : s.scenarioTab}
                onClick={() => setField("scenario", "new")}
              >
                New Patient (Referral)
              </button>
            </div>

            {form.scenario === "existing" ? (
              <>
                <select
                  style={s.input}
                  value={form.patientId}
                  onChange={(e) => setField("patientId", e.target.value)}
                >
                  {patients.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.firstName || ""} {p.lastName || ""} ({p.id})
                      {p.condition ? ` - ${p.condition}` : ""}
                    </option>
                  ))}
                </select>
                {selectedPatient && (
                  <div style={s.patientMeta}>
                    DOB: {selectedPatient.dob} &middot; {selectedPatient.treatment || "—"} &middot; {selectedPatient.physician || "—"}
                  </div>
                )}
              </>
            ) : (
              <div style={s.personaBlock}>
                <div style={s.labelHint}>
                  This is a referral intake scenario. The patient doesn't exist yet —
                  Sandy's create_patient tool will create them during the call.
                  Provide the identity details the synthetic caller should use.
                </div>
                <div style={s.personaRow}>
                  <div style={s.personaField}>
                    <label style={s.personaLabel}>First name *</label>
                    <input
                      style={s.input}
                      value={form.persona.firstName}
                      onChange={(e) => setPersonaField("firstName", e.target.value)}
                      placeholder="Jordan"
                    />
                  </div>
                  <div style={s.personaField}>
                    <label style={s.personaLabel}>Last name *</label>
                    <input
                      style={s.input}
                      value={form.persona.lastName}
                      onChange={(e) => setPersonaField("lastName", e.target.value)}
                      placeholder="Callahan"
                    />
                  </div>
                </div>
                <div style={s.personaRow}>
                  <div style={s.personaField}>
                    <label style={s.personaLabel}>Date of birth *</label>
                    <input
                      type="date"
                      style={s.input}
                      value={form.persona.dob}
                      onChange={(e) => setPersonaField("dob", e.target.value)}
                    />
                  </div>
                  <div style={s.personaField}>
                    <label style={s.personaLabel}>Condition (optional)</label>
                    <input
                      style={s.input}
                      value={form.persona.condition || ""}
                      onChange={(e) => setPersonaField("condition", e.target.value)}
                      placeholder="Referral — possible ACL tear"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Task Description */}
          <div style={s.field}>
            <label style={s.label}>Patient Task Description</label>
            <div style={s.labelHint}>Describe what the patient wants to accomplish on this call. This becomes the patient agent's instructions.</div>
            <textarea
              style={s.textarea}
              value={form.taskDescription}
              onChange={(e) => setField("taskDescription", e.target.value)}
              placeholder="e.g., You want to schedule a new Treatment Visit appointment sometime next week. When offered options, pick the first available slot."
              rows={5}
            />
          </div>

          {/* Expected Outcomes */}
          <div style={s.field}>
            <label style={s.label}>Expected Outcomes</label>
            <div style={s.labelHint}>Criteria the verification agent will check after the call.</div>
            {form.expectedOutcomes.map((outcome, idx) => (
              <div key={idx} style={s.outcomeRow}>
                <input
                  style={{ ...s.input, flex: 1 }}
                  value={outcome}
                  onChange={(e) => handleOutcomeChange(idx, e.target.value)}
                  placeholder="e.g., verify_patient called successfully"
                />
                <button style={s.removeBtn} onClick={() => removeOutcome(idx)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>
              </div>
            ))}
            <button style={s.addBtn} onClick={addOutcome}>+ Add outcome</button>
          </div>

          {/* Tags */}
          <div style={s.field}>
            <label style={s.label}>Tags</label>
            <div style={s.tagRow}>
              {form.tags.map((tag) => (
                <span key={tag} style={s.tag}>
                  {tag}
                  <span style={s.tagX} onClick={() => removeTag(tag)}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M18 6L6 18M6 6l12 12"/></svg>
                  </span>
                </span>
              ))}
              <input
                style={s.tagInput}
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                placeholder="Add tag..."
              />
            </div>
          </div>

          {/* Actions */}
          <div style={s.actions}>
            <button style={s.cancelBtn} onClick={() => navigate("dashboard")}>Cancel</button>
            <button style={s.saveBtn} onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Test Case"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const s = {
  page: {},
  aiSection: {
    marginBottom: 16,
  },
  aiToggle: {
    display: "flex", alignItems: "center", gap: 8, width: "100%",
    padding: "10px 14px", border: "1px solid #e5e5e5", borderRadius: 10,
    background: "#fafafa", fontSize: 14, fontWeight: 500, color: "#333",
    textAlign: "left",
  },
  aiChevron: { marginLeft: "auto", color: "#999", fontSize: 11 },
  aiBody: {
    padding: "14px", borderLeft: "1px solid #e5e5e5", borderRight: "1px solid #e5e5e5",
    borderBottom: "1px solid #e5e5e5", borderRadius: "0 0 10px 10px",
  },
  aiHint: { fontSize: 12, color: "#888", marginBottom: 10, lineHeight: 1.4 },
  aiInput: {
    width: "100%", padding: "10px 12px", border: "1px solid #e5e5e5", borderRadius: 8,
    fontSize: 14, outline: "none", resize: "vertical", fontFamily: "inherit",
    lineHeight: 1.5, color: "#0f0f0f", marginBottom: 10,
  },
  aiGenBtn: {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "8px 18px", background: "#0f0f0f", color: "#fff", border: "none",
    borderRadius: 8, fontSize: 13, fontWeight: 500,
  },
  aiGenBtnDisabled: {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "8px 18px", background: "#999", color: "#fff", border: "none",
    borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "not-allowed",
  },
  aiSpinner: {
    display: "inline-block", width: 12, height: 12, border: "2px solid #666",
    borderTopColor: "#fff", borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  header: {
    display: "flex", alignItems: "center", gap: 12,
    padding: "20px 32px", borderBottom: "1px solid #e5e5e5",
  },
  back: {
    width: 32, height: 32, border: "1px solid #e5e5e5", borderRadius: 8,
    background: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
    color: "#666",
  },
  title: { fontSize: 18, fontWeight: 600, letterSpacing: "-0.02em" },
  body: { padding: "24px 32px" },
  form: { maxWidth: 680 },
  row: { display: "flex", gap: 12, marginBottom: 16 },
  field: { marginBottom: 16 },
  fieldFull: { flex: 1, marginBottom: 0 },
  fieldSmall: { width: 100, marginBottom: 0 },
  label: { display: "block", fontSize: 13, fontWeight: 500, color: "#333", marginBottom: 6 },
  labelHint: { fontSize: 12, color: "#999", marginBottom: 6 },
  input: {
    width: "100%", padding: "8px 12px", border: "1px solid #e5e5e5", borderRadius: 8,
    fontSize: 14, outline: "none", color: "#0f0f0f", background: "#fff",
  },
  textarea: {
    width: "100%", padding: "10px 12px", border: "1px solid #e5e5e5", borderRadius: 8,
    fontSize: 14, outline: "none", resize: "vertical", fontFamily: "inherit",
    lineHeight: 1.5, color: "#0f0f0f",
  },
  patientMeta: { fontSize: 12, color: "#999", marginTop: 4 },
  scenarioTabs: {
    display: "flex", gap: 2, background: "#f5f5f5", borderRadius: 8,
    padding: 2, marginBottom: 8, width: "fit-content",
  },
  scenarioTab: {
    padding: "6px 14px", border: "none", borderRadius: 6,
    background: "transparent", fontSize: 13, fontWeight: 500, color: "#666",
    cursor: "pointer",
  },
  scenarioTabActive: {
    padding: "6px 14px", border: "none", borderRadius: 6,
    background: "#fff", fontSize: 13, fontWeight: 500, color: "#0f0f0f",
    cursor: "pointer", boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
  },
  personaBlock: {
    padding: 12, border: "1px dashed #e5e5e5", borderRadius: 8,
    background: "#fafafa",
  },
  personaRow: { display: "flex", gap: 10, marginBottom: 10 },
  personaField: { flex: 1 },
  personaLabel: {
    display: "block", fontSize: 11, fontWeight: 500, color: "#666", marginBottom: 4,
  },
  outcomeRow: { display: "flex", gap: 6, marginBottom: 6 },
  removeBtn: {
    width: 34, height: 34, border: "1px solid #e5e5e5", borderRadius: 8,
    background: "#fff", color: "#999", display: "flex", alignItems: "center",
    justifyContent: "center", flexShrink: 0,
  },
  addBtn: {
    padding: "4px 0", border: "none", background: "transparent",
    fontSize: 13, color: "#666", fontWeight: 500,
  },
  tagRow: {
    display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center",
    padding: "6px 10px", border: "1px solid #e5e5e5", borderRadius: 8, minHeight: 38,
  },
  tag: {
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "3px 10px", borderRadius: 10, background: "#f0f0f0",
    color: "#333", fontSize: 12, fontWeight: 500,
  },
  tagX: { cursor: "pointer", display: "flex", opacity: 0.5 },
  tagInput: {
    border: "none", outline: "none", fontSize: 13, flex: 1, minWidth: 80,
    background: "transparent", color: "#0f0f0f",
  },
  actions: {
    display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 24,
    paddingTop: 20, borderTop: "1px solid #e5e5e5",
  },
  cancelBtn: {
    padding: "8px 20px", border: "1px solid #e5e5e5", borderRadius: 8,
    background: "#fff", fontSize: 14, fontWeight: 500, color: "#666",
  },
  saveBtn: {
    padding: "8px 20px", background: "#0f0f0f", color: "#fff", border: "none",
    borderRadius: 8, fontSize: 14, fontWeight: 500,
  },
};
